import { useCallback, useEffect, useRef, useState } from 'react';
import { useTheme } from 'styled-components';
import {
  Anchor,
  Content,
  Icon,
  Inline,
  InlineItem,
  Pill,
  Portal,
  Secondary,
  Stack,
  Strong,
  useEventListener,
} from '@kinesis/bungle';
import { Dropzone, Hidden } from './file-uploader.styles';

// The drag-and-drop file prompt, ported from suit's `file-uploader`: a
// "Drag and drop / or browse" panel, a hidden `<input type=file>` behind the
// browse anchor, and a full-screen `Portal` drop overlay driven by
// window-level drag events. Purely a file *chooser* — the caller owns what
// happens with the chosen files (the transfer flow lives with the caller).
// Single-file: the input carries no `multiple` (suit's is multi-file; every
// square consumer today binds one document to one slot).
const FileUploader = (props: FileUploaderProps) => {
  const {
    background = 'gray',
    border = true,
    onChange,
    validFileExtensions = [],
    disabled = false,
  } = props;

  const theme = useTheme() as any;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dropzoneRef = useRef<HTMLDivElement>(null);

  const [showDropzone, setShowDropzone] = useState(false);
  const [fileCount, setFileCount] = useState(0);

  const handleChooseFileClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files) onChange(e.target.files);
    },
    [onChange],
  );

  // The overlay is shown imperatively by the drag handlers; hide it a beat
  // after the drag leaves so flickering between child elements (dragleave →
  // dragenter) doesn't strobe the overlay. Suit parity.
  useEffect(() => {
    const timerId = setTimeout(() => {
      if (!showDropzone && dropzoneRef.current)
        dropzoneRef.current.style.display = 'none';
    }, 200);

    return () => {
      clearTimeout(timerId);
    };
  }, [showDropzone]);

  useEventListener('dragenter', (e: DragEvent) => {
    if (disabled) return;
    setShowDropzone(true);
    setFileCount(e.dataTransfer?.items.length || 0);
    if (dropzoneRef.current) dropzoneRef.current.style.display = 'block';
  });

  useEventListener('dragover', (e: DragEvent) => {
    if (disabled) return;
    setShowDropzone(true);
    e.preventDefault();
  });

  useEventListener('dragleave', () => {
    setShowDropzone(false);
  });

  useEventListener(
    'drop',
    useCallback(
      (e: DragEvent) => {
        e.preventDefault();
        setShowDropzone(false);
        setFileCount(0);
        if (dropzoneRef.current) dropzoneRef.current.style.display = 'none';
        if (disabled) return;
        if (e.dataTransfer?.files?.length) onChange(e.dataTransfer.files);
      },
      [disabled, onChange],
    ),
  );

  return (
    <>
      <Content
        background={background}
        border={border}
        borderRadiusSize='small'
        padding='medium'
      >
        <Stack alignX='center' space='medium'>
          <Icon color={theme.color.gray6} magnitude='xxlarge' type='upload' />
          <Stack alignX='center' space='none'>
            <Strong>Drag and drop</Strong>
            <Secondary>
              or&nbsp;
              {disabled ? (
                <span
                  style={{
                    fontWeight: 500,
                    color: theme.color.gray6,
                  }}
                >
                  browse
                </span>
              ) : (
                <Anchor
                  as='button'
                  type='button'
                  onClick={handleChooseFileClick}
                >
                  browse
                </Anchor>
              )}
              &nbsp;to choose a file
            </Secondary>
          </Stack>
          {validFileExtensions.length > 0 && (
            <Inline space='small'>
              {validFileExtensions
                .filter((fileExtension) => fileExtension.label)
                .map((fileExtension: ValidFileExtension) => (
                  <InlineItem key={fileExtension.fileExtension}>
                    <Pill>{fileExtension.label}</Pill>
                  </InlineItem>
                ))}
            </Inline>
          )}
        </Stack>
      </Content>
      <Portal>
        <Dropzone ref={dropzoneRef}>
          Drop {fileCount === 1 ? 'file' : 'files'} to upload.
        </Dropzone>
      </Portal>
      <Hidden>
        <input
          accept={validFileExtensions
            .map((fileExtension) => fileExtension.fileExtension)
            .join()}
          onChange={handleInputChange}
          ref={fileInputRef}
          type='file'
        />
      </Hidden>
    </>
  );
};

// A file-extension the uploader accepts. `fileExtension` feeds the hidden
// input's `accept` (e.g. '.pdf'); `label` renders as a pill on the prompt.
export type ValidFileExtension = {
  label?: string;
  fileExtension: string;
};

export type FileUploaderProps = {
  onChange: (files: FileList) => void;
  validFileExtensions?: ValidFileExtension[];
  disabled?: boolean;
  border?: boolean;
  background?: React.ComponentProps<typeof Content>['background'];
};

export { FileUploader };
